import {
  type Client,
  type DaySequence,
  DaySequenceStatus,
  type Job,
  type User,
} from '@core/generated/gql-types';
import { formatBreakTime, getBreakTimeInMinutes } from '@/utils/utils';
import {
  EMPTY_CELL_LABEL,
  formatPersonName,
  formatTimeLabel,
  MS_PER_DAY,
  parseTimestamp,
  resolveJobRateCents,
} from '@/modules/payroll/utils/payrollFormat.utils';

export const STATUS_FILTERS: { key: DaySequenceStatus; label: string }[] = [
  { key: DaySequenceStatus.ReadyForPay, label: 'Ready for pay' },
  { key: DaySequenceStatus.RequiresApproval, label: 'Needs approval' },
  { key: DaySequenceStatus.PayProcessed, label: 'Pay processed' },
  { key: DaySequenceStatus.InProgress, label: 'In progress' },
  { key: DaySequenceStatus.ProposedChange, label: 'Has proposed change' },
  { key: DaySequenceStatus.EscalatedToTenant, label: 'Escalated to tenant' },
  { key: DaySequenceStatus.ErrorEncountered, label: 'Errors encountered' },
];

export type DaySequenceSortKey =
  | 'date'
  | 'day'
  | 'duration'
  | 'clockIn'
  | 'clockOut'
  | 'break'
  | 'payRate'
  | 'chargeRate'
  | 'status';

export type SortDirection = 'asc' | 'desc';

export type DateRange = { from: number | null; to: number | null };

export type DaySequenceLookups = {
  workers: Record<string, User>;
  jobs: Record<string, Job>;
  clients: Record<string, Client>;
};

export type DaySequenceRow = {
  sequence: DaySequence;
  timestamp: number | null;
  dayPosition: number;
  totalDays: number;
  workerName: string;
  clientName: string;
  durationValue: number;
  clockInValue: number | null;
  clockOutValue: number | null;
  clockInLabel: string;
  clockOutLabel: string;
  breakMinutes: number;
  breakLabel: string;
  payRateCents: number | null;
  chargeRateCents: number | null;
};

export type EntitySummary = {
  label: string;
  id?: string;
  multiple: boolean;
};

export type DaySequenceJobGroup = {
  /** Real job id, or undefined for sequences without one. */
  jobId?: string;
  key: string;
  label: string;
  durationText: string;
  rows: DaySequenceRow[];
  workerSummary: EntitySummary;
  clientSummary: EntitySummary;
};

const ORPHAN_KEY_PREFIX = '__orphan__:';

/** Day sequences default to the last 14 days, through the end of today. */
export const getDefaultDaySequenceRange = (
  now = new Date(),
): { start: Date; end: Date } => {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  return {
    start: new Date(today.valueOf() - 14 * MS_PER_DAY),
    end: new Date(today.valueOf() + MS_PER_DAY - 1),
  };
};

const getSequenceTimestampSource = (sequence: DaySequence) =>
  sequence.startDate ??
  sequence.startDateISO ??
  sequence.dateTime ??
  sequence.clockInTime ??
  sequence.endDate ??
  null;

export const getSequenceTimestamp = (sequence: DaySequence): number | null =>
  parseTimestamp(getSequenceTimestampSource(sequence));

export const formatSequenceDuration = (sequence: DaySequence): string => {
  if (!sequence.shiftDuration) {
    return EMPTY_CELL_LABEL;
  }
  return `${sequence.shiftDuration.toFixed(2)} ${sequence.shiftDurationUnit.toLowerCase()}`;
};

export const filterDaySequences = (
  sequences: DaySequence[],
  {
    range,
    statuses,
    searchText,
    lookups,
  }: {
    range: DateRange;
    statuses: Set<DaySequenceStatus>;
    searchText: string;
    lookups: DaySequenceLookups;
  },
): DaySequence[] => {
  const searchValue = searchText.trim().toLowerCase();

  return sequences.filter((sequence) => {
    const timestamp = getSequenceTimestamp(sequence);
    if (range.from !== null && (timestamp === null || timestamp < range.from)) {
      return false;
    }
    if (range.to !== null && (timestamp === null || timestamp > range.to)) {
      return false;
    }
    if (statuses.size > 0 && !statuses.has(sequence.status)) {
      return false;
    }
    if (!searchValue) {
      return true;
    }
    const worker = lookups.workers[sequence.userId ?? ''];
    const job = lookups.jobs[sequence.jobId];
    const client = lookups.clients[sequence.clientId ?? ''];
    return [
      sequence.id,
      sequence.userId,
      sequence.jobId,
      sequence.clientId,
      worker?.firstName,
      worker?.lastName,
      worker?.email,
      job?.title,
      job?.classification?.name,
      client?.name,
    ].some((value) => value?.toLowerCase().includes(searchValue));
  });
};

const getRowSortValue = (
  row: DaySequenceRow,
  key: DaySequenceSortKey,
): number | string => {
  switch (key) {
    case 'day':
      return row.dayPosition;
    case 'duration':
      return row.durationValue;
    case 'clockIn':
      return row.clockInValue ?? 0;
    case 'clockOut':
      return row.clockOutValue ?? 0;
    case 'break':
      return row.breakMinutes;
    case 'payRate':
      return row.payRateCents ?? 0;
    case 'chargeRate':
      return row.chargeRateCents ?? 0;
    case 'status':
      return row.sequence.status;
    case 'date':
    default:
      return row.timestamp ?? 0;
  }
};

const compareValues = (
  a: number | string | null,
  b: number | string | null,
): number =>
  typeof a === 'number' && typeof b === 'number'
    ? a - b
    : `${a ?? ''}`.localeCompare(`${b ?? ''}`);

const buildEntitySummary = (
  names: string[],
  ids: Array<string | null | undefined>,
  entity: 'worker' | 'client',
): EntitySummary => {
  const uniqueNames = Array.from(new Set(names.filter(Boolean)));
  const uniqueIds = Array.from(
    new Set(ids.filter((id): id is string => Boolean(id))),
  );
  if (uniqueNames.length === 1) {
    return {
      label: uniqueNames[0],
      id: uniqueIds.length === 1 ? uniqueIds[0] : undefined,
      multiple: false,
    };
  }
  if (uniqueNames.length === 0) {
    return { label: `Unknown ${entity}`, multiple: true };
  }
  return {
    label: `Multiple ${entity}s (${uniqueNames.length})`,
    multiple: true,
  };
};

/**
 * Groups sequences by job. Rows are numbered chronologically ("Day 2 of 5"),
 * then sorted within each group; groups are ordered by their first row.
 */
export const groupDaySequencesByJob = (
  sequences: DaySequence[],
  lookups: DaySequenceLookups,
  sortKey: DaySequenceSortKey,
  sortDirection: SortDirection,
): DaySequenceJobGroup[] => {
  const byJob = new Map<string, DaySequence[]>();
  sequences.forEach((sequence) => {
    const key = sequence.jobId?.trim()
      ? sequence.jobId
      : `${ORPHAN_KEY_PREFIX}${sequence.id}`;
    byJob.set(key, [...(byJob.get(key) ?? []), sequence]);
  });

  const directed = (result: number) =>
    sortDirection === 'asc' ? result : -result;

  return Array.from(byJob.entries())
    .map(([key, groupSequences]) => {
      const jobId = key.startsWith(ORPHAN_KEY_PREFIX) ? undefined : key;
      const job = jobId ? lookups.jobs[jobId] : undefined;
      const classification = job?.classification?.name?.trim();
      const title = job?.title?.trim();
      const label =
        classification && title
          ? `${classification} • ${title}`
          : (classification ?? title ?? 'Job');
      const payRateCents = resolveJobRateCents(job, 'worker');
      const chargeRateCents = resolveJobRateCents(job, 'client');
      const chronological = [...groupSequences].sort(
        (a, b) =>
          (getSequenceTimestamp(a) ?? 0) - (getSequenceTimestamp(b) ?? 0),
      );
      const totalDays = Math.max(
        job?.durationInDays ?? chronological.length,
        1,
      );

      const rows: DaySequenceRow[] = chronological.map((sequence, index) => {
        const clockInSource = sequence.clockInTime ?? job?.clockInTime ?? null;
        const clockOutSource =
          sequence.clockOutTime ?? job?.clockOutTime ?? null;
        const breaks =
          sequence.status === DaySequenceStatus.ProposedChange &&
          sequence.proposedBreaks?.length
            ? sequence.proposedBreaks
            : sequence.breaks;
        return {
          sequence,
          timestamp: getSequenceTimestamp(sequence),
          dayPosition: index + 1,
          totalDays,
          workerName: formatPersonName(lookups.workers[sequence.userId ?? '']),
          clientName:
            lookups.clients[sequence.clientId ?? '']?.name ??
            sequence.clientId ??
            EMPTY_CELL_LABEL,
          durationValue: sequence.shiftDuration ?? 0,
          clockInValue: parseTimestamp(clockInSource),
          clockOutValue: parseTimestamp(clockOutSource),
          clockInLabel: formatTimeLabel(clockInSource),
          clockOutLabel: formatTimeLabel(clockOutSource),
          breakMinutes: getBreakTimeInMinutes(breaks ?? null),
          breakLabel: formatBreakTime(breaks ?? null),
          payRateCents,
          chargeRateCents,
        };
      });

      const sortedRows = [...rows].sort((a, b) => {
        const result = compareValues(
          getRowSortValue(a, sortKey),
          getRowSortValue(b, sortKey),
        );
        return directed(
          result !== 0 ? result : (a.timestamp ?? 0) - (b.timestamp ?? 0),
        );
      });

      const days = job?.durationInDays ?? chronological.length;

      return {
        jobId,
        key,
        label,
        durationText: days
          ? `${days} ${days === 1 ? 'day' : 'days'} scheduled`
          : '',
        rows: sortedRows,
        workerSummary: buildEntitySummary(
          rows.map((row) => row.workerName),
          rows.map((row) => row.sequence.userId),
          'worker',
        ),
        clientSummary: buildEntitySummary(
          rows.map((row) => row.clientName),
          rows.map((row) => row.sequence.clientId),
          'client',
        ),
      };
    })
    .sort((a, b) => {
      const result = compareValues(
        a.rows[0] ? getRowSortValue(a.rows[0], sortKey) : null,
        b.rows[0] ? getRowSortValue(b.rows[0], sortKey) : null,
      );
      return result !== 0
        ? directed(result)
        : (a.rows[0]?.timestamp ?? 0) - (b.rows[0]?.timestamp ?? 0);
    });
};
